/**
 * Datetime utilities for consistent UTC handling across the frontend.
 *
 * Golden Rule:
 * - Store: UTC always
 * - Transmit: UTC always
 * - Display: Convert to user's timezone
 * - Input: Convert from user's timezone to UTC
 */

/**
 * Format a UTC datetime string for display in user's local timezone
 *
 * @param {string} utcString - ISO datetime string in UTC (e.g., "2025-12-04T11:01:00Z")
 * @param {object} options - Formatting options
 * @returns {string} Formatted datetime string in user's local timezone
 *
 * @example
 * formatDateTime('2025-12-04T11:01:00Z')
 * // → "Dec 4, 2025, 6:01 AM" (if user is in EST)
 */
export const formatDateTime = (utcString, options = {}) => {
  if (!utcString) return '';

  const date = new Date(utcString);

  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };

  return date.toLocaleString('en-US', defaultOptions);
};

/**
 * Format just the date part (no time)
 *
 * @param {string} utcString - ISO datetime string in UTC
 * @returns {string} Formatted date string
 *
 * @example
 * formatDate('2025-12-04T11:01:00Z')
 * // → "Dec 4, 2025"
 */
export const formatDate = (utcString) => {
  if (!utcString) return '';

  const date = new Date(utcString);

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format just the time part (no date)
 *
 * @param {string} utcString - ISO datetime string in UTC
 * @returns {string} Formatted time string
 *
 * @example
 * formatTime('2025-12-04T11:01:00Z')
 * // → "6:01 AM" (if user is in EST)
 */
export const formatTime = (utcString) => {
  if (!utcString) return '';

  const date = new Date(utcString);

  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Convert a datetime-local input value to UTC ISO string
 * This is used when sending datetime from forms to the backend
 *
 * @param {string} localDatetimeString - Datetime string from datetime-local input
 * @returns {string} UTC ISO string
 *
 * @example
 * // User in EST selects "2025-12-04 06:01" in UI
 * toUTCString('2025-12-04T06:01')
 * // → "2025-12-04T11:01:00.000Z" (converted to UTC)
 */
export const toUTCString = (localDatetimeString) => {
  if (!localDatetimeString) return '';

  const date = new Date(localDatetimeString);
  return date.toISOString();
};

/**
 * Convert a UTC ISO string to datetime-local input format
 * This is used when populating datetime forms from backend data
 *
 * @param {string} utcString - ISO datetime string in UTC
 * @returns {string} Local datetime string for datetime-local input
 *
 * @example
 * // Backend sends "2025-12-04T11:01:00Z"
 * toLocalInputString('2025-12-04T11:01:00Z')
 * // → "2025-12-04T06:01" (for EST user, for input field)
 */
export const toLocalInputString = (utcString) => {
  if (!utcString) return '';

  const date = new Date(utcString);

  // Format for datetime-local input: YYYY-MM-DDTHH:mm
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Get current datetime in UTC ISO format
 * Use this when you need "now" to send to backend
 *
 * @returns {string} Current UTC datetime as ISO string
 *
 * @example
 * getNowUTC()
 * // → "2025-12-02T19:30:00.000Z"
 */
export const getNowUTC = () => {
  return new Date().toISOString();
};

/**
 * Format date and time separately for event display
 *
 * @param {string} utcString - ISO datetime string in UTC
 * @returns {object} Object with formatted date and time
 *
 * @example
 * formatEventDateTime('2025-12-04T11:01:00Z')
 * // → { date: "Dec 4, 2025", time: "6:01 AM" }
 */
export const formatEventDateTime = (utcString) => {
  return {
    date: formatDate(utcString),
    time: formatTime(utcString)
  };
};

/**
 * Check if a datetime is in the past (compared to now in UTC)
 *
 * @param {string} utcString - ISO datetime string in UTC
 * @returns {boolean} True if datetime is in the past
 */
export const isPast = (utcString) => {
  if (!utcString) return false;
  return new Date(utcString) < new Date();
};

/**
 * Check if a datetime is in the future (compared to now in UTC)
 *
 * @param {string} utcString - ISO datetime string in UTC
 * @returns {boolean} True if datetime is in the future
 */
export const isFuture = (utcString) => {
  if (!utcString) return false;
  return new Date(utcString) > new Date();
};
